import { useQuery } from '@tanstack/react-query';
import { type ReactNode } from 'react';
import { type OrderPaymentState } from '@aps/contracts';
import {
  Chip,
  ContactCard,
  EmptyState,
  PriceTag,
  ShipmentRow,
  Skeleton,
  formatMoney,
} from '@aps/ui';
import { useNavigation } from '../app/navigation.js';
import { useSettings } from '../app/settings.js';
import { type MessageKey } from '../i18n/messages.js';
import { api } from '../lib/api.js';
import { openTelegramLink } from '../lib/telegram.js';
import { useContact } from '../lib/use-contact.js';

const PAYMENT_LABEL: Record<OrderPaymentState, MessageKey> = {
  UNPAID: 'order.paymentUnpaid',
  PARTIAL: 'order.paymentPartial',
  PAID: 'order.paymentPaid',
  OVERPAID: 'order.paymentOverpaid',
};

/**
 * 2-QATLAM: bitta buyurtma.
 *
 * Har jo'natma uchun sana va BIR SO'ZLI holat. Progress bar yo'q —
 * treker faqat 3-qatlamda, chuqurlashuv sifatida.
 */
export function OrderScreen({ id }: { id: string }): ReactNode {
  const { t, locale } = useSettings();
  const { push } = useNavigation();
  const contact = useContact();

  const order = useQuery({ queryKey: ['order', id], queryFn: () => api.order(id) });

  if (order.isPending) {
    return (
      <div className="aps-screen">
        <Skeleton height="32px" width="50%" />
        <Skeleton height="72px" />
      </div>
    );
  }
  if (order.data === undefined) return <EmptyState title={t('common.error')} />;

  const data = order.data;

  return (
    <div className="aps-screen">
      <header className="aps-screen__header">
        <h1 className="aps-screen__title">
          {t('order.title')} #{data.number}
        </h1>
        <PriceTag value={data.grandTotal} locale={locale} size="sm" showDiscount={false} />
      </header>

      <div className="aps-row">
        <Chip>{t(PAYMENT_LABEL[data.paymentState])}</Chip>
        {data.paymentState === 'PARTIAL' ? (
          <span className="aps-shipment__status">
            {formatMoney(data.paidTotal, locale)} / {formatMoney(data.grandTotal, locale)}
          </span>
        ) : null}
      </div>

      <section className="aps-stack">
        <h2 className="aps-section-title">{t('order.shipments')}</h2>
        {data.shipments.map((shipment) => (
          <ShipmentRow
            key={shipment.id}
            shipment={shipment}
            locale={locale}
            onSelect={() => {
              push({ name: 'shipment', id: shipment.id });
            }}
          />
        ))}
      </section>

      <section className="aps-stack">
        <h2 className="aps-section-title">{t('order.contact')}</h2>
        <p>{data.customerName}</p>
        <p className="aps-shipment__status">{data.customerPhone}</p>
        {data.addressLine === null ? null : (
          <p className="aps-shipment__status">
            {data.regionNameUz === null ? '' : `${data.regionNameUz}, `}
            {data.addressLine}
          </p>
        )}
      </section>

      {/* Buyurtmadan keyin savol ehtiyoji eng kuchli: yetkazish, moslik, holat. */}
      <ContactCard
        title={t('help.orderTitle')}
        description={t('help.orderBody')}
        hours={contact.hours}
        phone={contact.phone}
        telegram={contact.telegram}
        writeLabel={t('help.write')}
        telegramMessage={`${t('help.orderMessage')} ${data.number}`}
        onOpenTelegram={openTelegramLink}
      />

      {/* Kelishilgan yetkazish sharti shu yerda — tizimda narx yo'q. */}
      {data.operatorNote === null ? null : (
        <section className="aps-stack">
          <h2 className="aps-section-title">{t('order.operatorNote')}</h2>
          <p>{data.operatorNote}</p>
        </section>
      )}
    </div>
  );
}
